import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  ListRequest,
  useSamplesViewModelListQuery,
} from "@/services/backend/samples/api/sample-api";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { SampleViewModel } from "@/services/backend/samples/types/sample-view-model";
import t from "@/lang/lang";
import { ReactNode, useState } from "react";
import { LaboratoryName } from "@/services/backend/samples/components/laboratory-name";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import { UUID } from "@bitory-gmbh/nidavellir";
import { useSamplesDeliveryShowQuery } from "@/services/backend/samples/api/delivery-api";
import { LaboratoryFilter } from "./laboratory-filter";
import { DeliveryFilter } from "./delivery-filter";
import { CreateDeliveryDialog } from "./create-delivery-dialog";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { formatDateTime } from "@/shared/lib/utilities/date";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { EditDeliveryDialog } from "./edit-delivery-dialog";
import { AddToDeliveryDropdown } from "./add-to-delivery-dropdown";
import { DeleteSamplesDialog, SampleAssignment } from "./delete-samples-dialog";

export function SamplesForDeliveryCard({
  initialRequest,
  relatedEntity,
  documentId,
  documentNamespace,
  getAssignment,
}: {
  initialRequest: ListRequest;
  relatedEntity?: (sample: SampleViewModel) => ReactNode;
  documentId: UUID;
  documentNamespace: string;
  getAssignment?: (sample: SampleViewModel) => SampleAssignment | undefined;
}) {
  const [request, setRequest] = useState<ListRequest>({
    offset: 0,
    limit: 10,
    ...initialRequest,
  });
  const [selected, setSelected] = useState<SampleViewModel[]>([]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Proben zur Lieferung")}</CardTitle>
        <CardAction>
          <div className="flex gap-2">
            <SampleViewModelTableFilters
              request={request}
              onRequestChange={setRequest}
            />
            <Separator orientation="vertical" className="min-h-8" />
            <SampleViewModelTableActions
              selected={selected}
              onSelectedChange={setSelected}
              documentId={documentId}
              documentNamespace={documentNamespace}
              getAssignment={getAssignment}
            />
          </div>
        </CardAction>
      </CardHeader>
      <CardContent>
        <SampleViewModelTable
          request={request}
          onRequestChange={setRequest}
          relatedEntity={relatedEntity}
          selected={selected}
          onSelectedChange={setSelected}
        />
      </CardContent>
    </Card>
  );
}

function SampleViewModelTable({
  request,
  onRequestChange,
  relatedEntity,
  selected,
  onSelectedChange,
  hidePagination = false,
}: {
  request: ListRequest;
  onRequestChange: (r: ListRequest) => void;
  relatedEntity?: (sample: SampleViewModel) => ReactNode;
  selected: SampleViewModel[];
  onSelectedChange: (selected: SampleViewModel[]) => void;
  hidePagination?: boolean;
}) {
  const { data, isLoading, error } = useSamplesViewModelListQuery(request);

  return (
    <div className="grid gap-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("")}</TableHead>
            <TableHead>{t("Nummer")}</TableHead>
            <TableHead>{t("Art")}</TableHead>
            <TableHead>{t("Ort")}</TableHead>
            <TableHead>{t("Stelle")}</TableHead>
            {relatedEntity && <TableHead>{t("Positionen")}</TableHead>}
            <TableHead>{t("Labor")}</TableHead>
            <TableHead>{t("Lieferung")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && <TableLoadingRow colSpan={5} />}
          {error && <TableRtkErrorRow error={error} colSpan={5} />}
          {data && data.items.length === 0 && <TableEmptyRow colSpan={6} />}
          {data &&
            data.items.map((sample: SampleViewModel) => (
              <TableRow key={sample.id}>
                <TableCell>
                  <Checkbox
                    checked={selected.some((s) => s.id === sample.id)}
                    onCheckedChange={(state) => {
                      switch (state) {
                        case true:
                          onSelectedChange(
                            selected.some((s) => s.id === sample.id)
                              ? selected
                              : [...selected, sample],
                          );
                          break;
                        case false:
                          onSelectedChange(
                            selected.filter((s) => s.id !== sample.id),
                          );
                          break;
                        default:
                        // do nothing
                      }
                    }}
                  />
                </TableCell>
                <TableCell>{sample.number}</TableCell>
                <TableCell>{t(sample.kind)}</TableCell>
                <TableCell>{sample.location}</TableCell>
                <TableCell>{sample.spot}</TableCell>
                {relatedEntity && (
                  <TableCell>{relatedEntity(sample)}</TableCell>
                )}
                <TableCell>
                  {sample.labId && <LaboratoryName id={String(sample.labId)} />}
                </TableCell>
                <TableCell className="p-0">
                  <div className="grid">
                    {sample.deliveryIds?.map((id) => (
                      <DeliveryEdit key={id} id={id} />
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {!hidePagination && data && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={data.totalCount}
          setLimitOffset={(limit, offset) => {
            onRequestChange({ ...request, limit, offset });
          }}
          className="ml-auto"
        />
      )}
    </div>
  );
}

function DeliveryEdit({ id }: { id: string }) {
  const { data } = useSamplesDeliveryShowQuery({ id });
  if (!data) return null;
  return (
    <EditDeliveryDialog
      delivery={data}
      trigger={
        <Button
          size="sm"
          variant="link"
          className="m-0 h-6 p-0"
        >{`${formatDateTime(data.createdAt)}`}</Button>
      }
    />
  );
}

function SampleViewModelTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (r: ListRequest) => void;
}) {
  return (
    <div className="flex gap-2">
      <DeliveryFilter
        filter={request.delivery}
        onFilterChange={(delivery) => onRequestChange({ ...request, delivery })}
        document={request.document}
        isNullOption={t("Keine Lieferung")}
      />
      <LaboratoryFilter
        filter={request.laboratory}
        onFilterChange={(laboratory) =>
          onRequestChange({ ...request, laboratory })
        }
        isNullOption={t("Kein Labor")}
      />
    </div>
  );
}

function SampleViewModelTableActions({
  selected,
  onSelectedChange,
  documentId,
  documentNamespace,
  getAssignment,
}: {
  selected: SampleViewModel[];
  onSelectedChange: (selected: SampleViewModel[]) => void;
  documentId: UUID;
  documentNamespace: string;
  getAssignment?: (sample: SampleViewModel) => SampleAssignment | undefined;
}) {
  return (
    <div className="flex gap-2">
      <DeleteSamplesDialog
        samples={selected}
        onSuccess={() => onSelectedChange([])}
        getAssignment={getAssignment}
      />
      <AddToDeliveryDropdown selected={selected} />
      <CreateDeliveryDialog
        selected={selected}
        documentId={documentId}
        documentNamespace={documentNamespace}
        onSuccess={() => onSelectedChange([])}
      />
    </div>
  );
}
